import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import axios from "axios";
import { CurrencyProvider } from "./context/CurrencyContext";

import ProtectedRoute from "./ProtectedRoute";   // <-- IMPORTANT

import Index from "./pages/Index";
import Products from "./pages/Product";
import ProductGroup from "./pages/productgroup";
import Productdetails from "./pages/Productdetails";
import Login from "./pages/Login";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PartnerRegistration from "./pages/PartnerRegistration";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerAccount from "./pages/partneraccount";
import Orders from "./pages/orders";
import OrderDetails from "./pages/orderdetails";
import Cart from "./pages/Cart";
import Checkout from "./pages/checkout";
import Partnerpricebracket from "./pages/Partnerpricebracket";
import About from "./pages/about";
import Blog from "./pages/blog";
import BlogDetails from "./pages/blogDetails";
import Contact from "./pages/contact";
import EngagementInsurance from "./pages/engagementInsurance";
import Faq from "./pages/faq";
import Finance from "./pages/finance";
import FreeResize from "./pages/freeResize";
import PrivacyPolicy from "./pages/privacypolicy";
import Returns from "./pages/return";
import ShippingPolicy from "./pages/shippingpolicy";
import { Image_URL, API_BASE_URL } from "./config";

import ProductCustomization from "./pages/ProductCustomization";
import Customiseorderlist from "./pages/customiseorderlist";
import Customiseorder from "./pages/customiseorder";
import Customiseproduct from "./pages/customiseproduct";
import Return from "./pages/return";
import Wishlist from "./pages/wishlist";
import StockMaster from "./pages/Stockcarry";
import Requestpriceview from "./pages/Requestforpriceview"
import Requestpricedetails from "./pages/Requestpricedetails"
import Stockcarryorder from "./pages/stockcarryorder"
import Stockcarrydetails from "./pages/stockcarrydetails"
import Carrycart from "./pages/carrycart";
import CarryCheckout from "./pages/carrycheckout";
import Productview from "./pages/ProductView";
import Stockcarryview from "./pages/stockcarryview";
import AppUpdater from "./components/AppUpdater";
const App = () => {

  // MUST start with null, NOT empty string
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // FIX: this is now the ONLY place that sets document.title/favicon, and
  // it's reactive on `user` itself — not called imperatively from inside
  // applyUser/clearUser. That means it ALWAYS reflects whichever user is
  // currently in state, no matter which code path set it (the fast
  // cache-restore below, a full GetProfile response, a fresh login, or a
  // logout) — and because it's a separate effect that runs on every
  // render where `user` changed, it also wins any race against an
  // individual page (like Products) setting its own static document.title
  // on mount, since this effect re-asserts the correct value whenever
  // `user` itself changes.
  useEffect(() => {
    const DEFAULT_TITLE = "West End Jewellery";
    const DEFAULT_FAVICON = "/assets/img/West-End-Jewelry-Logo1.png";

    const setFavicon = (url) => {
      document
        .querySelectorAll("link[rel='shortcut icon'], link[rel='icon']")
        .forEach((el) => el.remove());
      const link = document.createElement("link");
      link.rel = "shortcut icon";
      link.type = "image/x-icon";
      link.href = url;
      document.head.appendChild(link);
    };

    document.title = user?.companyName || DEFAULT_TITLE;
    setFavicon(user?.companyLogo ? `${Image_URL}Companylogo/${user.companyLogo}` : DEFAULT_FAVICON);
  }, [user]);

  // FIXED: localStorage is per-origin, so a user logged in on
  // westendjewellery.com showed as logged-out on nz.westendjewellery.com
  // even though the shared auth_token cookie was valid there too.
  // We now always verify the session against the API (which reads the
  // shared cookie) instead of trusting localStorage blindly. localStorage
  // is still used as a cache so the UI doesn't flash "logged out" while
  // the API call is in flight.
  useEffect(() => {
    // ONE-TIME CLEANUP: users who were already logged in BEFORE this fix
    // shipped still have old localStorage/sessionStorage sitting around
    // (cart/orders/etc. left by whoever used this browser previously). New
    // logins already get a clean slate (Login.js clears storage on
    // submit), but existing sessions never go through that path again — so
    // we force it once here, the first time this code runs on a browser.
    // No manual action needed from any of the 1000 users — this fires
    // automatically the next time each of them opens the site.
    //
    // This does NOT log the user out: GetProfile below re-authenticates
    // using the shared auth cookie (which this clear does not touch) and
    // re-populates a fresh "user" entry straight from the server.
    const CLEANUP_FLAG = "storageCleanupV1";
    if (!localStorage.getItem(CLEANUP_FLAG)) {
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem(CLEANUP_FLAG, "true");
    }

    const applyUser = (userData) => {
      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("loginTime", new Date().getTime().toString());
      // title/favicon are now handled by the useEffect above (reactive on `user`)
    };

    const clearUser = () => {
      // FIX: clear everything, not just "user"/"loginTime" — any other
      // page-specific cached data (cart, orders, wishlist, etc.) left in
      // localStorage/sessionStorage from that session must not survive
      // into whoever logs in next on this browser. Re-set the cleanup
      // flag right after so this doesn't re-trigger the block above.
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem(CLEANUP_FLAG, "true");
      setUser(null);
      // title/favicon reset is handled by the useEffect above (reactive on `user`)
    };

    const checkSession = async () => {
      // Show cached user immediately (avoids a logged-out flash) while
      // we confirm with the server in the background.
      const cachedUser = localStorage.getItem("user");
      if (cachedUser) {
        try {
          setUser(JSON.parse(cachedUser));
        } catch {
          // ignore corrupt cache, server check below will fix it
        }
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/Home/GetProfile`, {
          withCredentials: true, // sends the shared .westendjewellery.com cookie
        });

        if (response.data && response.data.success) {
  applyUser(response.data.data);
} else {
  clearUser();
}
      } catch (err) {
        // No valid cookie / session expired / API unreachable
        clearUser();
      } finally {
        setLoadingUser(false);
      }
    };

    checkSession();
  }, []);

  // Block rendering until user loads from localStorage
  if (loadingUser) return <div></div>;

  return (
    <> <AppUpdater />
    <CurrencyProvider user={user}>
      <Router>
        <Routes>

          {/* PUBLIC ROUTES */}
          <Route path="/" element={<Index />} />
          <Route path="/Login" element={<Login setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/registration" element={<PartnerRegistration />} />
<Route path="/contact" element={<Contact />} />
<Route path="/about" element={<About />} />
          {/* PROTECTED ROUTES */}
          <Route
            path="/wishlist"
            element={
              <ProtectedRoute user={user}>
                <Wishlist />
              </ProtectedRoute>
            }
          />

          <Route
  path="/products"
  element={
    <ProtectedRoute user={user}>
      <Products />
    </ProtectedRoute>
  }
/>
  <Route
  path="/productgroup"
  element={
    <ProtectedRoute user={user}>
      <ProductGroup />
    </ProtectedRoute>
  }
/>

          <Route
            path="/productdetails/:id"
            element={
              <ProtectedRoute user={user}>
                <Productdetails user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cart"
            element={
              <ProtectedRoute user={user}>
                <Cart />
              </ProtectedRoute>
            }
          />

          <Route
            path="/partnerdashboard"
            element={
              <ProtectedRoute user={user}>
                <PartnerDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/partneraccount"
            element={
              <ProtectedRoute user={user}>
                <PartnerAccount />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders"
            element={
              <ProtectedRoute user={user}>
                <Orders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orderdetails/:orderId/:orderItemId"
            element={
              <ProtectedRoute user={user}>
                <OrderDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout"
            element={
              <ProtectedRoute user={user}>
                <Checkout />
              </ProtectedRoute>
            }
          />

          <Route
            path="/partnerpricebracket"
            element={
              <ProtectedRoute user={user}>
                <Partnerpricebracket />
              </ProtectedRoute>
            }
          />

          {/* <Route
            path="/about"
            element={
              <ProtectedRoute user={user}>
                <About />
              </ProtectedRoute>
            }
          /> */}

          <Route
            path="/blog"
            element={
              <ProtectedRoute user={user}>
                <Blog />
              </ProtectedRoute>
            }
          />

          <Route
            path="/blogDetails"
            element={
              <ProtectedRoute user={user}>
                <BlogDetails />
              </ProtectedRoute>
            }
          />

          {/* <Route
            path="/contactus"
            element={
              <ProtectedRoute user={user}>
                <Contact />
              </ProtectedRoute>
            }
          /> */}

          <Route
            path="/return"
            element={
              <ProtectedRoute user={user}>
                <Return />
              </ProtectedRoute>
            }
          />

          <Route
            path="/engagementInsurance"
            element={
              <ProtectedRoute user={user}>
                <EngagementInsurance />
              </ProtectedRoute>
            }
          />

          <Route
            path="/faq"
            element={
              <ProtectedRoute user={user}>
                <Faq />
              </ProtectedRoute>
            }
          />

          <Route
            path="/finance"
            element={
              <ProtectedRoute user={user}>
                <Finance />
              </ProtectedRoute>
            }
          />

          <Route
            path="/freeResize"
            element={
              <ProtectedRoute user={user}>
                <FreeResize />
              </ProtectedRoute>
            }
          />

          <Route
            path="/privacyPolicy"
            element={
              <ProtectedRoute user={user}>
                <PrivacyPolicy />
              </ProtectedRoute>
            }
          />

          <Route
            path="/returns"
            element={
              <ProtectedRoute user={user}>
                <Returns />
              </ProtectedRoute>
            }
          />

          <Route
            path="/shippingPolicy"
            element={
              <ProtectedRoute user={user}>
                <ShippingPolicy />
              </ProtectedRoute>
            }
          />

          <Route
            path="/productCustomization"
            element={
              <ProtectedRoute user={user}>
                <ProductCustomization />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customiseorder"
            element={
              <ProtectedRoute user={user}>
                <Customiseorder />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customiseorderlist/:userId/:orderId"
            element={
              <ProtectedRoute user={user}>
                <Customiseorderlist />
              </ProtectedRoute>
            }
          />

          <Route
            path="/customiseproduct"
            element={
              <ProtectedRoute user={user}>
                <Customiseproduct />
              </ProtectedRoute>
            }
          />
 <Route
            path="/stockandcarry"
            element={
              <ProtectedRoute user={user}>
                <StockMaster />
              </ProtectedRoute>
            }
          />
           <Route
            path="/Stockcarryview/:productcode"
            element={
              <ProtectedRoute user={user}>
                <Stockcarryview />
              </ProtectedRoute>
            }
          />
      

<Route
            path="/requestforpriceview"
            element={
              <ProtectedRoute user={user}>
                <Requestpriceview/>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requestpricedetails/:orderId"
            element={
              <ProtectedRoute user={user}>
                <Requestpricedetails/>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stockcarryorder"
            element={
              <ProtectedRoute user={user}>
                <Stockcarryorder/>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stockcarrydetails/:orderId/:orderItemId"
            element={
              <ProtectedRoute user={user}>
                <Stockcarrydetails/>
              </ProtectedRoute>
            }
          />
           <Route
            path="/carrycart"
            element={
              <ProtectedRoute user={user}>
                <Carrycart />
              </ProtectedRoute>
            }
          />
           <Route
            path="/carrycheckout"
            element={
              <ProtectedRoute user={user}>
                <CarryCheckout />
              </ProtectedRoute>
            }
          />
          
        </Routes>


      </Router>
    </CurrencyProvider>
    </>
  );
};

export default App;





// import React, { useState, useEffect } from "react";
// import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
// import { CurrencyProvider } from "./context/CurrencyContext";

// import ProtectedRoute from "./ProtectedRoute";   // <-- IMPORTANT

// import Index from "./pages/Index";
// import Products from "./pages/Product";
// import Productdetails from "./pages/Productdetails";
// import Login from "./pages/Login";
// import ForgotPassword from "./components/ForgotPassword";
// import ResetPassword from "./components/ResetPassword";

// import PartnerRegistration from "./pages/PartnerRegistration";
// import PartnerDashboard from "./pages/PartnerDashboard";
// import PartnerAccount from "./pages/partneraccount";
// import Orders from "./pages/orders";
// import OrderDetails from "./pages/orderdetails";
// import Cart from "./pages/Cart";
// import Checkout from "./pages/checkout";
// import Partnerpricebracket from "./pages/Partnerpricebracket";
// import About from "./pages/about";
// import Blog from "./pages/blog";
// import BlogDetails from "./pages/blogDetails";
// import Contact from "./pages/contact";
// import EngagementInsurance from "./pages/engagementInsurance";
// import Faq from "./pages/faq";
// import Finance from "./pages/finance";
// import FreeResize from "./pages/freeResize";
// import PrivacyPolicy from "./pages/privacypolicy";
// import Returns from "./pages/return";
// import ShippingPolicy from "./pages/shippingpolicy";
// import { Image_URL } from "./config";

// import ProductCustomization from "./pages/ProductCustomization";
// import Customiseorderlist from "./pages/customiseorderlist";
// import Customiseorder from "./pages/customiseorder";
// import Customiseproduct from "./pages/customiseproduct";
// import Return from "./pages/return";
// import Wishlist from "./pages/wishlist";
// import StockMaster from "./pages/Stockcarry";
// import Requestpriceview from "./pages/Requestforpriceview"
// import Requestpricedetails from "./pages/Requestpricedetails"
// import Stockcarryorder from "./pages/stockcarryorder"
// import Stockcarrydetails from "./pages/stockcarrydetails"
// import Carrycart from "./pages/carrycart";
// import CarryCheckout from "./pages/carrycheckout";
// import Productview from "./pages/ProductView";

// const App = () => {

//   // MUST start with null, NOT empty string
//   const [user, setUser] = useState(null);
//   const [loadingUser, setLoadingUser] = useState(true);

//   useEffect(() => {
//     const storedUser = localStorage.getItem("user");
//     const loginTime = localStorage.getItem("loginTime");

//     if (storedUser && loginTime) {
//       const currentTime = new Date().getTime();
//       const storedTime = parseInt(loginTime, 10);

//       // EXPIRE AFTER 1 HOUR
//       if (currentTime - storedTime > 3600000) {
//         localStorage.removeItem("user");
//         localStorage.removeItem("loginTime");
//         setUser(null);
//       } else {
//         const parsedUser = JSON.parse(storedUser);
//         setUser(parsedUser);

//         // Fix companyName case
//         document.title = parsedUser.companyName || "West End Jewellery";

//         // Fix companyLogo case
//         const favicon = document.querySelector("link[rel='shortcut icon']");
//         if (favicon && parsedUser.companyLogo) {
//           favicon.href = `${Image_URL}Companylogo/${parsedUser.companyLogo}`;
//         }

//         // Auto logout timer
//         setTimeout(() => {
//           localStorage.removeItem("user");
//           localStorage.removeItem("loginTime");
//           setUser(null);
//         }, 3600000 - (currentTime - storedTime));
//       }
//     }

//     // VERY IMPORTANT
//     setLoadingUser(false);
//   }, []);

//   // Block rendering until user loads from localStorage
//   if (loadingUser) return <div></div>;

//   return (
    
//     <CurrencyProvider>
//       <Router>
//         <Routes>

//           {/* PUBLIC ROUTES */}
//           <Route path="/" element={<Index />} />
//           <Route path="/Login" element={<Login setUser={setUser} />} />
//           <Route path="/forgot-password" element={<ForgotPassword />} />
//           <Route path="/reset-password" element={<ResetPassword />} />
//           <Route path="/registration" element={<PartnerRegistration />} />
// <Route path="/contactus" element={<Contact />} />
// <Route path="/about" element={<About />}/>
            
//           {/* PROTECTED ROUTES */}
//           <Route
//             path="/wishlist"
//             element={
//               <ProtectedRoute user={user}>
//                 <Wishlist />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//   path="/products"
//   element={
//     <ProtectedRoute user={user}>
//       <Products />
//     </ProtectedRoute>
//   }
// />

//           <Route
//             path="/productdetails/:id"
//             element={
//               <ProtectedRoute user={user}>
//                 <Productdetails user={user} />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/cart"
//             element={
//               <ProtectedRoute user={user}>
//                 <Cart />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/partnerdashboard"
//             element={
//               <ProtectedRoute user={user}>
//                 <PartnerDashboard />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/partneraccount"
//             element={
//               <ProtectedRoute user={user}>
//                 <PartnerAccount />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/orders"
//             element={
//               <ProtectedRoute user={user}>
//                 <Orders />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/orderdetails/:orderId/:orderItemId"
//             element={
//               <ProtectedRoute user={user}>
//                 <OrderDetails />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/checkout"
//             element={
//               <ProtectedRoute user={user}>
//                 <Checkout />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/partnerpricebracket"
//             element={
//               <ProtectedRoute user={user}>
//                 <Partnerpricebracket />
//               </ProtectedRoute>
//             }
//           />

          

//           <Route
//             path="/blog"
//             element={
//               <ProtectedRoute user={user}>
//                 <Blog />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/blogDetails"
//             element={
//               <ProtectedRoute user={user}>
//                 <BlogDetails />
//               </ProtectedRoute>
//             }
//           />

//           {/* <Route
//             path="/contactus"
//             element={
//               <ProtectedRoute user={user}>
//                 <Contact />
//               </ProtectedRoute>
//             }
//           /> */}

//           <Route
//             path="/return"
//             element={
//               <ProtectedRoute user={user}>
//                 <Return />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/engagementInsurance"
//             element={
//               <ProtectedRoute user={user}>
//                 <EngagementInsurance />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/faq"
//             element={
//               <ProtectedRoute user={user}>
//                 <Faq />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/finance"
//             element={
//               <ProtectedRoute user={user}>
//                 <Finance />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/freeResize"
//             element={
//               <ProtectedRoute user={user}>
//                 <FreeResize />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/privacyPolicy"
//             element={
//               <ProtectedRoute user={user}>
//                 <PrivacyPolicy />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/returns"
//             element={
//               <ProtectedRoute user={user}>
//                 <Returns />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/shippingPolicy"
//             element={
//               <ProtectedRoute user={user}>
//                 <ShippingPolicy />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/productCustomization"
//             element={
//               <ProtectedRoute user={user}>
//                 <ProductCustomization />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/customiseorder"
//             element={
//               <ProtectedRoute user={user}>
//                 <Customiseorder />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/customiseorderlist/:userId/:orderId"
//             element={
//               <ProtectedRoute user={user}>
//                 <Customiseorderlist />
//               </ProtectedRoute>
//             }
//           />

//           <Route
//             path="/customiseproduct"
//             element={
//               <ProtectedRoute user={user}>
//                 <Customiseproduct />
//               </ProtectedRoute>
//             }
//           />
//  <Route
//             path="/stockandcarry"
//             element={
//               <ProtectedRoute user={user}>
//                 <StockMaster />
//               </ProtectedRoute>
//             }
//           />
      

// <Route
//             path="/requestforpriceview"
//             element={
//               <ProtectedRoute user={user}>
//                 <Requestpriceview/>
//               </ProtectedRoute>
//             }
//           />
//           <Route
//             path="/requestpricedetails/:orderId"
//             element={
//               <ProtectedRoute user={user}>
//                 <Requestpricedetails/>
//               </ProtectedRoute>
//             }
//           />
//           <Route
//             path="/stockcarryorder"
//             element={
//               <ProtectedRoute user={user}>
//                 <Stockcarryorder/>
//               </ProtectedRoute>
//             }
//           />
//           <Route
//             path="/stockcarrydetails/:orderId/:orderItemId"
//             element={
//               <ProtectedRoute user={user}>
//                 <Stockcarrydetails/>
//               </ProtectedRoute>
//             }
//           />
//            <Route
//             path="/carrycart"
//             element={
//               <ProtectedRoute user={user}>
//                 <Carrycart />
//               </ProtectedRoute>
//             }
//           />
//            <Route
//             path="/carrycheckout"
//             element={
//               <ProtectedRoute user={user}>
//                 <CarryCheckout />
//               </ProtectedRoute>
//             }
//           />
          
//         </Routes>


//       </Router>
//     </CurrencyProvider>
//   );
// };

// export default App;